/**
 * reason-dialog.tsx — Hộp xác nhận thao tác Platform kèm LÝ DO (ghi vào nhật ký Platform).
 * `children` cho phép chèn thêm trường (chọn gói, số ngày…) phía trên ô lý do.
 */
import { useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { reasonError } from './reason'

export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Xác nhận',
  destructive,
  reasonRequired = true,
  pending,
  canConfirm = true,
  onConfirm,
  children,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  title: string
  description?: ReactNode
  confirmLabel?: string
  destructive?: boolean
  reasonRequired?: boolean
  pending?: boolean
  canConfirm?: boolean
  onConfirm: (reason: string) => Promise<void> | void
  children?: ReactNode
}) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()
  // C14: lý do tuỳ chọn = để trống hoặc ≥ 3 ký tự (khớp backend) — báo trước khi gửi.
  const error = reasonError(reason, reasonRequired)
  const reasonOk = error === null

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!reasonOk || !canConfirm) return
    await onConfirm(trimmed)
    setReason('')
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setReason(''); onOpenChange(v) }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {children}
          <div className="space-y-1.5">
            <Label htmlFor="platform-reason">
              Lý do {reasonRequired ? <span className="text-destructive">*</span> : '(tuỳ chọn)'}
            </Label>
            <Textarea
              id="platform-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ghi rõ lý do — lưu vào nhật ký quản trị Platform"
            />
            {error && trimmed.length > 0 && <p className="text-xs text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Huỷ</Button>
            <Button type="submit" variant={destructive ? 'destructive' : 'default'} disabled={!reasonOk || !canConfirm || pending}>
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
